import { apiSlice } from './apiSlice'

// ===================== settingsPreferencesApi =====================
/**
 * preferencesApi — generic per-user UI preferences.
 *
 * Backend: /user-preferences/{scope}/{key}
 * Returns BOTH default + user payloads; consumer (`usePreference` hook) merges
 * them. PATCH = deep-merge against user row. DELETE without sub_key resets the
 * whole user row; with sub_key clears just one nested entry.
 *
 * Tag tuple `(scope, key)` keyed cache so different (scope,key) pairs cache
 * independently. See `services/preferences/registry.py` for shape contracts.
 */

export interface PreferenceResponse {
  scope: string
  key: string
  default: Record<string, unknown>
  user: Record<string, unknown>
}

export interface PreferencePatchArg {
  scope: string
  prefKey: string
  patch: Record<string, unknown>
}

export interface PreferenceResetArg {
  scope: string
  prefKey: string
  subKey?: string
}

export interface PreferenceSeedArg {
  scope: string
  prefKey: string
  subKey?: string
}

export interface PreferenceSeedResponse {
  scope: string
  key: string
  sub_key: string | null
  /** 'seed-new' | 'merge-subkey' | 'drift-update' | 'skip-identical' */
  action: 'seed-new' | 'merge-subkey' | 'drift-update' | 'skip-identical'
}

const tagFor = (scope: string, prefKey: string) =>
  ({ type: 'Preference' as const, id: `${scope}/${prefKey}` })

const preferencesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPreference: builder.query<PreferenceResponse, { scope: string; prefKey: string }>({
      query: ({ scope, prefKey }) => ({
        url: `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}`,
      }),
      providesTags: (_res, _err, { scope, prefKey }) => [tagFor(scope, prefKey)],
    }),

    patchPreference: builder.mutation<{ message: string }, PreferencePatchArg>({
      query: ({ scope, prefKey, patch }) => ({
        url: `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}`,
        method: 'PATCH',
        body: { patch },
      }),
      onQueryStarted: async ({ scope, prefKey, patch }, { dispatch, queryFulfilled }) => {
        const undo = dispatch(
          preferencesApi.util.updateQueryData(
            'getPreference',
            { scope, prefKey },
            (draft) => {
              // No cached getPreference entry yet (e.g. query still pending or
              // errored) → draft is undefined; skip the optimistic merge.
              if (!draft) return
              draft.user = deepMerge(draft.user ?? {}, patch)
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          undo.undo()
        }
      },
    }),

    seedPreferenceDefault: builder.mutation<PreferenceSeedResponse, PreferenceSeedArg>({
      query: ({ scope, prefKey, subKey }) => {
        const base = `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}/seed-default`
        const qs = subKey ? `?sub_key=${encodeURIComponent(subKey)}` : ''
        return { url: `${base}${qs}`, method: 'POST' }
      },
      // Invalidate cached getPreference so next read reflects the (possibly) new default.
      invalidatesTags: (_res, _err, { scope, prefKey }) =>
        _res && _res.action !== 'skip-identical' ? [tagFor(scope, prefKey)] : [],
    }),

    resetPreference: builder.mutation<{ message: string }, PreferenceResetArg>({
      query: ({ scope, prefKey, subKey }) => {
        const base = `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}`
        return {
          url: subKey ? `${base}/${encodeURIComponent(subKey)}` : base,
          method: 'DELETE',
        }
      },
      onQueryStarted: async ({ scope, prefKey, subKey }, { dispatch, queryFulfilled }) => {
        const undo = dispatch(
          preferencesApi.util.updateQueryData(
            'getPreference',
            { scope, prefKey },
            (draft) => {
              if (!subKey) {
                draft.user = {}
              } else if (draft.user && typeof draft.user === 'object') {
                delete (draft.user as Record<string, unknown>)[subKey]
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          undo.undo()
        }
      },
    }),
  }),
})

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function deepMerge(base: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base }
  for (const [k, v] of Object.entries(patch)) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMerge(out[k] as Record<string, unknown>, v)
    } else {
      out[k] = v
    }
  }
  return out
}

export const {
  useGetPreferenceQuery,
  usePatchPreferenceMutation,
  useResetPreferenceMutation,
  useSeedPreferenceDefaultMutation,
} = preferencesApi

// ===================== settingsUserColumnVisibilityApi =====================
/**
 * userColumnVisibilityApi — per-user visible-columns-per-table persisted via backend.
 *
 * Single GET on app load returns visible columns for all tables, keyed by tableKey.
 * PUT per table_key writes one table's column list (avoids cross-table races).
 */

export type VisibleColumns = string[]
export type ColumnVisibilityDoc = Record<string, VisibleColumns>

export interface ColumnVisibilityResponse {
  columns: ColumnVisibilityDoc
}

export interface ColumnVisibilityPutPayload {
  tableKey: string
  columns: VisibleColumns
}

const userColumnVisibilityApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserColumnVisibility: builder.query<ColumnVisibilityDoc, void>({
      query: () => ({ url: '/user-preferences/column-visibility' }),
      transformResponse: (res: ColumnVisibilityResponse) => res?.columns ?? {},
      providesTags: ['UserColumnVisibility'],
    }),

    putUserColumnVisibility: builder.mutation<{ message: string }, ColumnVisibilityPutPayload>({
      query: ({ tableKey, columns }) => ({
        url: `/user-preferences/column-visibility/${encodeURIComponent(tableKey)}`,
        method: 'PUT',
        body: { columns },
      }),
      onQueryStarted: async ({ tableKey, columns }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userColumnVisibilityApi.util.updateQueryData('getUserColumnVisibility', undefined, (draft) => {
            draft[tableKey] = [...columns]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    deleteUserColumnVisibility: builder.mutation<{ message: string }, string>({
      query: (tableKey) => ({
        url: `/user-preferences/column-visibility/${encodeURIComponent(tableKey)}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (tableKey, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userColumnVisibilityApi.util.updateQueryData('getUserColumnVisibility', undefined, (draft) => {
            delete draft[tableKey]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
  }),
})

export const {
  useGetUserColumnVisibilityQuery,
  usePutUserColumnVisibilityMutation,
  useDeleteUserColumnVisibilityMutation,
} = userColumnVisibilityApi


// ===================== settingsUserPageFiltersApi =====================
/**
 * userPageFiltersApi — per-user dashboard-level filter state persisted via backend.
 */

export type PageFiltersValue = Record<string, any> | unknown[]
export type PageFiltersDoc = Record<string, PageFiltersValue>

export interface PageFiltersResponse {
  filters: PageFiltersDoc
}

export interface PageFiltersPutPayload {
  pageKey: string
  filters: PageFiltersValue
}

const userPageFiltersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserPageFilters: builder.query<PageFiltersDoc, void>({
      query: () => ({ url: '/user-preferences/page-filters' }),
      transformResponse: (res: PageFiltersResponse) => res?.filters ?? {},
      providesTags: ['UserPageFilters'],
    }),

    putUserPageFilters: builder.mutation<{ message: string }, PageFiltersPutPayload>({
      query: ({ pageKey, filters }) => ({
        url: `/user-preferences/page-filters/${encodeURIComponent(pageKey)}`,
        method: 'PUT',
        body: { filters },
      }),
      onQueryStarted: async ({ pageKey, filters }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userPageFiltersApi.util.updateQueryData('getUserPageFilters', undefined, (draft) => {
            draft[pageKey] = Array.isArray(filters) ? [...filters] : { ...filters }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    deleteUserPageFilters: builder.mutation<{ message: string }, string>({
      query: (pageKey) => ({
        url: `/user-preferences/page-filters/${encodeURIComponent(pageKey)}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (pageKey, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userPageFiltersApi.util.updateQueryData('getUserPageFilters', undefined, (draft) => {
            delete draft[pageKey]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
  }),
})

export const {
  useGetUserPageFiltersQuery,
  usePutUserPageFiltersMutation,
  useDeleteUserPageFiltersMutation,
} = userPageFiltersApi


// ===================== settingsUserTableWidthsApi =====================
/**
 * userTableWidthsApi — per-user column widths persisted via backend.
 *
 * Single GET on app load returns all widths keyed by table.
 * PUT per table_key writes one table's widths (avoids cross-table races).
 */

export type ColumnWidths = Record<string, number>
export type TableWidthsDoc = Record<string, ColumnWidths>

export interface TableWidthsResponse {
  widths: TableWidthsDoc
}

export interface TableWidthsPutPayload {
  tableKey: string
  widths: ColumnWidths
}

const userTableWidthsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserTableWidths: builder.query<TableWidthsDoc, void>({
      query: () => ({ url: '/user-preferences/table-widths' }),
      transformResponse: (res: TableWidthsResponse) => res?.widths ?? {},
      providesTags: ['UserTableWidths'],
    }),

    putUserTableWidths: builder.mutation<{ message: string }, TableWidthsPutPayload>({
      query: ({ tableKey, widths }) => ({
        url: `/user-preferences/table-widths/${encodeURIComponent(tableKey)}`,
        method: 'PUT',
        body: { widths },
      }),
      onQueryStarted: async ({ tableKey, widths }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userTableWidthsApi.util.updateQueryData('getUserTableWidths', undefined, (draft) => {
            draft[tableKey] = { ...widths }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    deleteUserTableWidths: builder.mutation<{ message: string }, string>({
      query: (tableKey) => ({
        url: `/user-preferences/table-widths/${encodeURIComponent(tableKey)}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (tableKey, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userTableWidthsApi.util.updateQueryData('getUserTableWidths', undefined, (draft) => {
            delete draft[tableKey]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
  }),
})

export const {
  useGetUserTableWidthsQuery,
  usePutUserTableWidthsMutation,
  useDeleteUserTableWidthsMutation,
} = userTableWidthsApi

